
import { Button, Link, MenuItem, Popover, Select, Typography, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TextFilterOperator } from "components/shared/filters";

import { MutableRefObject, useEffect, useState } from "react";
import { GridApi, GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid-pro";
import { WorkItemStatuses } from "../fields/WorkItemStatuses"; 
import CheckboxColumn from "./CheckboxColumn";
import { useFeatureEnabled } from "hooks/shared";

const getSpansWithChangedAssignments = (params: any) => {
  let assignmentMap = new Array<{ key: string, primary?: string, alternate?: string }>();
  params?.params?.row?.responsibilitiesResources?.forEach((responsibilityWithResources: any) => {
    responsibilityWithResources.resources.forEach((resource: any) => {
      if (resource.selected == true && resource.isDefaultAssignee == false) {
        assignmentMap.push({ key: responsibilityWithResources.responsibilityName, primary: resource.userName });
      }
      if (!resource.selected && resource.isNew) {
        assignmentMap.push({ key: responsibilityWithResources.responsibilityName, alternate: resource.userName });
      }
    });
  });

  let spanList: any[] = [];

  params?.params?.row?.responsibilitiesResources?.forEach((responsibilityWithResource: any) => {
    const changedItems = assignmentMap.filter(x => x.key === responsibilityWithResource.responsibilityName);
    changedItems.forEach(item => {
      if (item.primary) {
        spanList.push(<span>{responsibilityWithResource.responsibilityName + ': (P) ' + item.primary}</span>);
      } else {
        spanList.push(<span>{responsibilityWithResource.responsibilityName + ': ' + item.alternate}</span>);
      }
      spanList.push(<br />);
    });
  });
  return spanList;
}

function UnsavedChangesPopover(params: any) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const spans = getSpansWithChangedAssignments(params);

  return (spans.length > 0 ? 
    <div>
       <Link
        style={{ color: 'black', cursor: 'pointer', textDecoration: 'underline' }}
        component="button" underline="hover" onClick={handleClick}>
        View
      </Link>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Typography sx={{ p: 2 }}>
          {spans}
        </Typography>
      </Popover>
    </div> : <></>
  )
}

const useWorkItemResourcesColumns = (onChange: (changeType: string, params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>, event: any) => void, apiRef: MutableRefObject<GridApi>, WorkItemPermissions : any) => {
  const [ wave4Enabled ] = useFeatureEnabled('wave-4');
  const columnBase: GridColDef[] = [
    CheckboxColumn(apiRef),
    {
      field: 'workItemInstanceName',
      headerName: 'Work Item Name',
      flex: 1,
      minWidth: 300,
      sortable: true,
      resizable: true,
      hideable: false,
      headerClassName: 'header',
      hideSortIcons: false,
      disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.row.workItemInstanceName;
        const workItemId = params.row.workItemInstanceId;
        const workItemUrl = process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/overview`;
        return (
          <Tooltip title={name} arrow>
            <div>
              <a href={workItemUrl}
                style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }}
                rel="noopener noreferrer">
                {name}
              </a>
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'workspaceInstanceName',
      headerName: 'Workspace',
      width: 150,
      sortable: true,
      resizable: true,
      hideable: true,
      headerClassName: 'header',
      hideSortIcons: false
    },
    {
      field: 'contentTypeName',
      headerName: 'Content type',
      width: 150, sortable: true,
      resizable: true,
      hideable: true,
      headerClassName: 'header',
      hideSortIcons: false
    },
    {
      field: 'workItemStatusId',
      headerName: 'Status',
      width: 150,
      sortable: true,
      resizable: true,
      hideable: false,
      headerClassName: 'header',
      hideSortIcons: false,
      filterOperators: TextFilterOperator,
      editable: false,
      type: 'singleSelect',
      valueOptions: WorkItemStatuses,
    },
    {
      field: 'responsibilityId',
      headerName: 'Responsibility',
      width: 200,
      sortable: false,
      resizable: true,
      hideable: true,
      headerClassName: 'header',
      hideSortIcons: true,
      renderCell: (params) => {
        if (params.row.responsibilitiesResources === undefined ||
          params.row.responsibilitiesResources.length === 0) {
          return (<></>);
        }
        return (
          <Select value={params.row.selectedResponsibilityId}
            style={{width: '100%'}}
            
            onChange={(event) => onChange('responsibility', params, event)}>
            {
              params.row.responsibilitiesResources.map((responsibilitiesResource: any) => (
                <MenuItem
                  key={responsibilitiesResource.responsibilityId}
                  value={responsibilitiesResource.responsibilityId}>
                  {responsibilitiesResource.responsibilityName}
                </MenuItem>
              ))
            }
          </Select>
        )
      }
    },
    {
      field: 'resources',
      headerName: 'Resource',
      width: 200,
      sortable: false,
      resizable: true,
      hideable: true,
      headerClassName: 'header',
      hideSortIcons: true,
      renderCell: (params) => {
        if (params.row.responsibilitiesResources === undefined ||
          params.row.responsibilitiesResources.length === 0) 
        {
          return (<></>);
        }
        const resources_array = params.row.responsibilitiesResources.find((responsibilitiesResource: any) => responsibilitiesResource.responsibilityId === params.row.selectedResponsibilityId).resources;
        const resource = params.row.responsibilitiesResources.find((obj: any) => obj.responsibilityId === params.row.selectedResponsibilityId).resources.find((resource: any) => resource.selected == true);
        const resourceId = resource === undefined ? "" : resource.userId;
        return (
          <Select value={resourceId }
            style={{width: '100%'}}
            onChange={(event) => onChange('resource', params, event)}          >
            {
              resources_array.map((resource: any) => (
                <MenuItem
                  disabled={resource.userName === '++++' && resource.isDefaultAssignee === false}
                  key={resource.userId}
                  value={resource.userId}>
                  {resource.selected ? resource.userName !== "" && '(P) ' + resource.userName : resource.userName}
                </MenuItem>
              ))
            }
          </Select>
        )
      }
    },
    {
      field: 'pendingChanges',
      headerName: 'Pending Changes',
      width: 150,
      sortable: false,
      resizable: true,
      hideable: true,
      headerClassName: 'header',
      hideSortIcons: true,
      renderCell: (params) => {
        if (params.row.rowChanged === true) {
          return <UnsavedChangesPopover params={params} />;
        }
      }
    }
  ];

  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);
    setColumns(newColumns);
  }, []);

  return columns;
};

export default useWorkItemResourcesColumns;